// Topping the wallet up online from a venue's page, where the café takes online payments and a wallet: a few amounts
// in one tap, any other whole amount between the café's limits, and one button that starts the payment. The player then
// leaves for the payment provider's page and comes back to `/payment-complete`. Nothing is added to the wallet until the
// provider confirms; the café's limits and extras are the server's, shown here as it sent them.

import type { MyTopUpOptions, MyVenuePassShop, NonNegativeMoney } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, TextField } from '@clavian/ui-web';
import { useMutation } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { problemCode } from '../api/problems';
import { formatterFor } from '../lib/format';
import { bestTopUpBonus, checkTypedAmount, goToCheckout } from '../lib/payments';

const newKey = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

function failureMessage(error: unknown, t: (key: string) => string): string {
  switch (problemCode(error)) {
    case 'online_payments_unavailable':
      return t('onlineTopUp.failure.onlineOff');
    case 'wallet_disabled':
      return t('onlineTopUp.failure.walletOff');
    case 'wallet_limit':
      return t('onlineTopUp.failure.limit');
    case 'payment_provider_unavailable':
      return t('onlineTopUp.failure.providerDown');
    default:
      return t('onlineTopUp.failure.generic');
  }
}

export function OnlineTopUp({ shop, options }: { readonly shop: MyVenuePassShop; readonly options: MyTopUpOptions }): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const fmt = formatterFor(options.min.currency);
  const [preset, setPreset] = useState<number | null>(null);
  const [typed, setTyped] = useState('');
  const [key, setKey] = useState(newKey);

  const checked = typed.trim() === '' ? null : checkTypedAmount(typed, options.min, options.max);
  const typedError =
    checked === null || checked.ok
      ? undefined
      : checked.reason === 'tooLow'
        ? t('onlineTopUp.tooLow', { min: fmt.money(options.min) })
        : checked.reason === 'tooHigh'
          ? t('onlineTopUp.tooHigh', { max: fmt.money(options.max) })
          : t('onlineTopUp.notWhole');
  const chosen: NonNegativeMoney | null =
    checked !== null ? (checked.ok ? checked.money : null) : preset === null ? null : { amount: preset, currency: options.min.currency };
  const bonus = chosen === null ? 0 : bestTopUpBonus(chosen.amount, options.offers);

  const start = useMutation({
    mutationFn: (amount: NonNegativeMoney) =>
      api.beginMyOnlineTopUp({ path: { venueId: shop.venue.id }, headerParams: { 'idempotency-key': key }, body: { amount } }),
    onSuccess: (payment) => goToCheckout({ id: payment.id, purpose: 'wallet_topup', venueSlug: shop.venue.slug }, payment.checkoutUrl),
  });

  const changed = (): void => {
    setKey(newKey());
    start.reset();
  };

  return (
    <section className="pw-topup" aria-labelledby="pw-topup-title">
      <h3 className="pw-topup__title" id="pw-topup-title">
        {t('onlineTopUp.title')}
      </h3>
      <p className="pw-muted">{t('onlineTopUp.body')}</p>
      <ul className="pw-topup__amounts" aria-label={t('onlineTopUp.amounts')}>
        {options.presets.map((amount) => {
          const extra = bestTopUpBonus(amount.amount, options.offers);
          const selected = checked === null && preset === amount.amount;
          return (
            <li key={amount.amount}>
              <button
                type="button"
                className="pw-topup__amount"
                aria-pressed={selected}
                onClick={() => {
                  setPreset(amount.amount);
                  setTyped('');
                  changed();
                }}
              >
                <span className="pw-topup__figure clav-num">{fmt.money(amount)}</span>{' '}
                {extra > 0 ? <span className="pw-topup__extra">{t('onlineTopUp.extra', { amount: fmt.money({ amount: extra, currency: amount.currency }) })}</span> : null}
              </button>
            </li>
          );
        })}
      </ul>
      <TextField
        label={t('onlineTopUp.other')}
        description={t('onlineTopUp.otherHint', { min: fmt.money(options.min), max: fmt.money(options.max) })}
        error={typedError}
        numeric
        inputMode="numeric"
        autoComplete="off"
        value={typed}
        onChange={(event) => {
          setTyped(event.target.value);
          setPreset(null);
          changed();
        }}
      />
      {chosen !== null && checked !== null && bonus > 0 ? (
        <p className="pw-topup__extra-line">{t('onlineTopUp.extra', { amount: fmt.money({ amount: bonus, currency: chosen.currency }) })}</p>
      ) : null}
      {start.isError ? (
        <p className="pw-topup__error" role="alert">
          {failureMessage(start.error, t)}
        </p>
      ) : null}
      <Button variant="primary" block disabled={chosen === null} busy={start.isPending} onClick={() => (chosen === null ? undefined : start.mutate(chosen))}>
        {chosen === null ? t('onlineTopUp.choose') : t('onlineTopUp.pay', { amount: fmt.money(chosen) })}
      </Button>
    </section>
  );
}
